/* Modal-Host: zeigt React-Dialoge im vorhandenen #modal-Element. Dialoge liegen übereinander (z. B. Anmeldung über den Einstellungen);
 * die unteren bleiben erhalten (ausgeblendet), damit ihr Zustand nicht verloren geht. openDialog liefert das Ergebnis des Dialogs. */
import type { ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { clampDrag } from './dragpos.ts';

type Close<T> = (result?: T) => void;
interface Entry { id: number; render: (close: Close<never>) => ReactNode; resolve: (v: unknown) => void }

let entries: Entry[] = [];
let nextId = 1;
let root: Root | null = null;
let host: HTMLElement | null = null;
const listeners = new Set<() => void>();
const subscribe = (f: () => void) => { listeners.add(f); return () => { listeners.delete(f); }; };
const getEntries = () => entries;
const emit = () => listeners.forEach((f) => f());

function Frame(props: { children: ReactNode; active: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!props.active || !ref.current) return;
    const f = ref.current.querySelector<HTMLElement>('input, [role="tab"][aria-selected="true"], button');
    if (f) f.focus();
  }, [props.active]);
  // Verschieben an der Titelleiste (nicht am Schließen-Knopf); der Versatz bleibt beim Wiederöffnen desselben Dialogs bestehen, solange er offen ist
  const off = useRef({ x: 0, y: 0 });
  const drag = useRef<{ px: number; py: number; ox: number; oy: number; left: number; top: number; width: number } | null>(null);
  const apply = () => { if (ref.current) ref.current.style.transform = off.current.x || off.current.y ? `translate(${off.current.x}px, ${off.current.y}px)` : ''; };
  const onDown = (ev: React.PointerEvent<HTMLDivElement>) => {
    const t = ev.target as HTMLElement;
    if (ev.button !== 0 || !ref.current || !t.closest('.vlv-title') || t.closest('button, input, select, a')) return;
    const r = ref.current.getBoundingClientRect();
    drag.current = { px: ev.clientX, py: ev.clientY, ox: off.current.x, oy: off.current.y, left: r.left - off.current.x, top: r.top - off.current.y, width: r.width };
    ev.currentTarget.setPointerCapture(ev.pointerId);
    ev.preventDefault();
  };
  const onMove = (ev: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    off.current = clampDrag({ left: d.left, top: d.top, width: d.width }, d.ox + ev.clientX - d.px, d.oy + ev.clientY - d.py, { w: window.innerWidth, h: window.innerHeight });
    apply();
  };
  const onUp = (ev: React.PointerEvent<HTMLDivElement>) => { if (drag.current) { drag.current = null; if (ev.currentTarget.hasPointerCapture(ev.pointerId)) ev.currentTarget.releasePointerCapture(ev.pointerId); } };
  return <div ref={ref} className="box valve" role="dialog" aria-modal="true" style={props.active ? undefined : { display: 'none' }} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>{props.children}</div>;
}

function Stack() {
  const list = useSyncExternalStore(subscribe, getEntries);
  return <>{list.map((e, i) => <Frame key={e.id} active={i === list.length - 1}>{e.render((r) => closeEntry(e.id, r))}</Frame>)}</>;
}

function ensureRoot(): void {
  if (!host) host = document.getElementById('modal');
  if (!host) throw new Error('#modal fehlt');
  if (!root) {
    host.textContent = '';
    root = createRoot(host);
    root.render(<Stack />);
    host.onclick = (ev) => { if (ev.target === host) closeTop(); };
  }
  host.hidden = false;
}

function closeEntry(id: number, result?: unknown): void {
  const e = entries.find((x) => x.id === id);
  if (!e) return;
  entries = entries.filter((x) => x.id !== id);
  e.resolve(result);
  if (!entries.length && host) host.hidden = true;
  emit();
}

/** Öffnet einen Dialog über allen anderen. close(ergebnis) schließt ihn und löst das Versprechen auf. */
export function openDialog<T = void>(render: (close: Close<T>) => ReactNode): Promise<T | undefined> {
  ensureRoot();
  return new Promise((resolve) => {
    entries = entries.concat([{ id: nextId++, render: render as (close: Close<never>) => ReactNode, resolve: resolve as (v: unknown) => void }]);
    emit();
  });
}
/** Schließt den obersten Dialog (Esc, Klick auf den Hintergrund) */
export function closeTop(): void { const e = entries[entries.length - 1]; if (e) closeEntry(e.id); }
/** Schließt alle React-Dialoge (ohne Ergebnis) */
export function closeAllDialogs(): void { [...entries].reverse().forEach((e) => closeEntry(e.id)); }
export const hasDialogs = (): boolean => entries.length > 0;
/** Gibt das #modal-Element für DOM-Dialoge frei: schließt alle React-Dialoge und entfernt den React-Baum */
export function releaseModal(): void {
  closeAllDialogs();
  if (root) { root.unmount(); root = null; if (host) host.onclick = null; }
  host = null;
}
